import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import MapComponent from '../common/MapComponent';
import { Autocomplete } from '@react-google-maps/api';
import { auth } from '../../firebase';
import axios from 'axios';
import { API_URL, SOCKET_URL } from '../../apiConfig';

const IMGBB_API_KEY = '1ff966068ee0b54aa8d6b3cc8b6d492e';

const NGORequestForm = ({ onSubmit, isLoaded, loadError, MAP_ID }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        peopleNeeded: 1,
        category: 'Food',
        location: null,
        points: 10,
        eventDate: '',
        eventTime: '',
    });
    const [beforeImageFile, setBeforeImageFile] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 24.8607, lng: 67.0011 });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const autocompleteRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = ['peopleNeeded', 'points'].includes(name) ? parseInt(value, 10) : value;
        setFormData(prev => ({ ...prev, [name]: parsedValue }));
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setBeforeImageFile(e.target.files[0]);
        }
    };

    const handleMapClick = (e) => {
        const newLocation = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setFormData({ ...formData, location: newLocation });
        setError('');
    };

    const handlePlaceSelect = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry) {
                const newLocation = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };
                setFormData({ ...formData, location: newLocation });
                setMapCenter(newLocation);
                setError('');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.location || !formData.eventDate || !formData.eventTime) {
            setError('Please fill out all fields, including date, time, and location.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("You must be logged in to post a request.");
            const idToken = await user.getIdToken();
            
            let beforeImageUrl = '';
            if (beforeImageFile) {
                const imgbbFormData = new FormData();
                imgbbFormData.append('image', beforeImageFile);
                const imgbbResponse = await axios.post(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, imgbbFormData);
                beforeImageUrl = imgbbResponse.data.data.url;
            }

            const requestPayload = { ...formData, beforeImageUrl };
            
            const response = await axios.post(`${API_URL}/requests`, requestPayload, {
                headers: { Authorization: `Bearer ${idToken}` },
            });
            onSubmit(response.data);
            // Reset form
            setFormData({
                title: '', description: '', peopleNeeded: 1, category: 'Food',
                location: null, points: 10, eventDate: '', eventTime: '',
            });
            setBeforeImageFile(null);
        } catch (err) {
            console.error("Error posting request:", err);
            // --- BUG FIX: Ensure error is always a string ---
            const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || "An unexpected error occurred.";
            setError(String(errorMessage));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div 
            className="bg-white rounded-xl p-6 shadow-md border border-gray-200"
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
                    <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Volunteers Needed</label>
                        <input type="number" name="peopleNeeded" min="1" value={formData.peopleNeeded} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Redeemable Points</label>
                        <input type="number" name="points" min="1" value={formData.points} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                    </div>
                </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Event Date</label>
                        <input type="date" name="eventDate" value={formData.eventDate} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Event Time</label>
                        <input type="time" name="eventTime" value={formData.eventTime} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" required />
                    </div>
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
                    <select name="category" value={formData.category} onChange={handleChange} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black">
                        <option>Food</option>
                        <option>Elderly Care</option>
                        <option>Environment</option>
                        <option>Education</option>
                        <option>Animal Welfare</option>
                        <option>Health</option>
                        <option>Others</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">"Before" Image (Optional)</label>
                    <input type="file" onChange={handleFileChange} accept="image/*" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"/>
                    {beforeImageFile && <p className="text-xs text-gray-500 mt-1">Selected: {beforeImageFile.name}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Location</label>
                    {isLoaded ? (
                        <Autocomplete
                            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                            onPlaceChanged={handlePlaceSelect}
                        >
                            <input
                                type="text"
                                placeholder="Search for a location"
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black mb-2"
                            />
                        </Autocomplete>
                    ) : <div>Loading search...</div>}
                    <div className="h-48 w-full rounded-lg overflow-hidden">
                        <MapComponent 
                           isLoaded={isLoaded}
                           loadError={loadError}
                           center={mapCenter}
                           onMapClick={handleMapClick}
                           singleMarkerPosition={formData.location}
                           MAP_ID={MAP_ID}
                        />
                    </div>
                    {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
                    {formData.location && !error && (
                        <p className="text-sm text-gray-500 mt-2">
                            Location Selected: {formData.location.lat.toFixed(4)}, {formData.location.lng.toFixed(4)}
                        </p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all disabled:bg-gray-500"
                >
                    {submitting ? 'Posting...' : 'Post Request'}
                </button>
            </form>
        </motion.div>
    );
};

export default NGORequestForm;
